import type { ResearchJobEvent } from '@/types/generated/research'

export interface ResearchPhaseStep {
  phase: string
  label: string
  durationSeconds: number
}

// Derives a step-by-step timing breakdown from the poll's phase-kind events — only
// `kind === 'phase'` events carry a `phase` milestone (see ResearchJobEvent's doc comment;
// absent/empty `kind` means a "tool" event, never a phase one, even though that reads like it
// should be included). Each returned step's duration is measured from its own event's `ts_ms`
// to the NEXT phase event's `ts_ms`. The currently-active (last) phase has no "next" yet, so
// it's deliberately excluded here — the caller renders it live at the top of the status line
// instead of listing it as a completed step in this history.
export function buildCompletedPhaseSteps(events: ResearchJobEvent[]): ResearchPhaseStep[] {
  const phaseEvents = events
    .filter((event) => event.kind === 'phase' && !!event.phase)
    .sort((a, b) => a.seq - b.seq)

  const steps: ResearchPhaseStep[] = []
  for (let i = 0; i < phaseEvents.length - 1; i++) {
    const current = phaseEvents[i]
    const next = phaseEvents[i + 1]
    const startMs = Number(current.tsMs)
    const endMs = Number(next.tsMs)
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue
    steps.push({
      phase: current.phase as string,
      label: current.label || (current.phase as string),
      durationSeconds: Math.max(0, Math.round((endMs - startMs) / 1000)),
    })
  }
  return steps
}

// Every phase of a finished run, the last one included. That last phase has no next event to
// end it, so it takes whatever the run's wall-clock duration (ReportMeta.durationSeconds) leaves
// after the earlier phases; without a duration it's measured to the run's latest event of any
// kind. Feeds the trace line kept above a delivered report.
export function buildDeliveredPhaseSteps(
  events: ResearchJobEvent[],
  runDurationSeconds?: number
): ResearchPhaseStep[] {
  const steps = buildCompletedPhaseSteps(events)
  const last = events
    .filter((event) => event.kind === 'phase' && !!event.phase)
    .sort((a, b) => a.seq - b.seq)
    .at(-1)
  if (!last) return steps

  const earlierSeconds = steps.reduce((sum, step) => sum + step.durationSeconds, 0)
  const latestMs = Math.max(...events.map((event) => Number(event.tsMs)).filter(Number.isFinite))
  const lastSeconds =
    runDurationSeconds !== undefined
      ? Math.round(runDurationSeconds) - earlierSeconds
      : Math.round((latestMs - Number(last.tsMs)) / 1000)
  return [
    ...steps,
    {
      phase: last.phase as string,
      label: last.label || (last.phase as string),
      durationSeconds: Number.isFinite(lastSeconds) ? Math.max(0, lastSeconds) : 0,
    },
  ]
}

/** "48s", "1m 12s", "2m". */
export function formatStepDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`
}
